import React, { useEffect, useRef } from 'react';
import { Box, Toolbar, Alert, Button } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../components/headers/Header';
import Sidebar from '../components/Sidebar';
import QuickTour from '../components/tour/QuickTour';
import { TourProvider } from '../context/TourContext';
import { FREE_PLAN_ID } from '../utils/common';
import { httpService } from '../services/httpService';
import { decryptResponse, encryptResponse, getSessionKey, REQ_ENC_KEY } from '../services';
import { setPlanInfo } from '../features/auth/authSlice';

export default function MainLayout({ children }: React.PropsWithChildren<{}>) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: any) => state.auth.userDetails);
  const navigate = useNavigate();
  const location = useLocation();
  const planDetails = userDetails?.plan_details;
  const isFreePlan = planDetails?.plan_id === FREE_PLAN_ID;

  // Keep scroll position when only deep params change on Get Started
  // (e.g. platform/kit), and reset when moving to a different page/product.
  const scrollResetKey = location.pathname.startsWith('/get-started/')
    ? location.pathname.split('/').slice(0, 3).join('/')
    : location.pathname;

  const getFreePlanStatus = (startDate: string, trialPeriod: number) => {
    const start = new Date(startDate);
    const now = new Date();
    const daysSinceStart = Math.floor((now.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
    const daysLeft = trialPeriod - daysSinceStart;
    return {
      isExpiringSoon: daysLeft <= 5 && daysLeft >= 0,
      isExpired: daysLeft < 0,
      daysLeft,
    };
  };

  const getMonthlyPlanStatus = (endDate?: string) => {
    if (!endDate) return null;

    const end = new Date(endDate);
    const now = new Date();
    const daysLeft = Math.ceil((end.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));

    return {
      isExpiringSoon: daysLeft <= 5 && daysLeft >= 0,
      isExpired: daysLeft < 0,
      daysLeft,
    };
  };

  const fetchOrgPlanInfo = async () => {
    if (!userDetails?.organization_id) return;

    try {
      const payload = {
        data: await encryptResponse({
          organization_id: userDetails.organization_id,
        }, REQ_ENC_KEY),
      };
      const response = await httpService.post<any>('/user/getOrgPlanDetails', payload);
      if (response.data.status === 200 && response.data.data) {
        const decryptedData: any = await decryptResponse(response.data.data, getSessionKey() as string);
        const planInfo = decryptedData.plan;
        if (planInfo?.plan_details) {
          dispatch(setPlanInfo(planInfo.plan_details));
        }
      }
    } catch (error) {
      console.error('Error fetching org plan details:', error);
    }
  };

  const freePlanStatus = isFreePlan
    ? getFreePlanStatus(planDetails?.plan_start_date, userDetails?.trial_period)
    : null;
  const showFreePlanNotification = Boolean(
    isFreePlan && (freePlanStatus?.isExpiringSoon || freePlanStatus?.isExpired)
  );

  const isMonthlyPaidPlan = !isFreePlan && planDetails?.billing_cycle === 'monthly';
  const monthlyPlanStatus = isMonthlyPaidPlan
    ? getMonthlyPlanStatus(planDetails?.billing_end_date)
    : null;
  const showMonthlyPlanNotification = Boolean(
    monthlyPlanStatus?.isExpiringSoon || monthlyPlanStatus?.isExpired
  );

  const showPlanNotification = showFreePlanNotification || showMonthlyPlanNotification;
  const planStatus = showFreePlanNotification ? freePlanStatus : monthlyPlanStatus;
  const notificationMessage = showFreePlanNotification
    ? (planStatus?.isExpired
      ? 'Your plan has expired. Please upgrade your plan.'
      : 'Your free plan expires soon. Please upgrade your plan.')
    : (planStatus?.isExpired
      ? 'Your plan has expired. Please upgrade your plan.'
      : 'Your plan expires soon. Please upgrade your plan.');

  useEffect(() => {
    fetchOrgPlanInfo();
  }, [userDetails?.organization_id]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [scrollResetKey]);

  return (
    <TourProvider>
      <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
        <Sidebar />
        <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.default', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Header />
          <Toolbar />
          {showPlanNotification && (
            <Alert
              severity="warning"
              action={
                <Button color="inherit" size="small" onClick={() => navigate('/plans-and-billing')}>
                  Upgrade Plan
                </Button>
              }
            >
              {notificationMessage}
            </Alert>
          )}
          <Box
            ref={scrollRef}
            data-main-scroll="true"
            sx={{ p: 3, overflowY: 'auto', flexGrow: 1 }}
          >
            {children}
          </Box>
        </Box>
        <QuickTour />
      </Box>
    </TourProvider>
  );
}
